// utils/exportUtils.js
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const formatCurrency = (value) =>
  `R$ ${parseFloat(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  // Fix for YYYYMMDD raw strings (like 20260301)
  if (/^\d{8}$/.test(dateStr)) {
    const y = dateStr.slice(0, 4);
    const m = dateStr.slice(4, 6);
    const d = dateStr.slice(6, 8);
    return `${d}/${m}/${y}`;
  }
  // Fix: datas ISO "YYYY-MM-DD" são interpretadas como UTC 00:00
  // Ao converter para pt-BR (UTC-3) ficaria no dia anterior.
  // Solução: forçar meio-dia local para neutralizar o offset de timezone.
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    try {
      const d = new Date(dateStr + 'T12:00:00');
      return d.toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  }
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR');
  } catch {
    return dateStr;
  }
};

// ── CSV ──────────────────────────────────────────────────────────────────────
export const exportToCSV = (transactions, balances, period, currentUser) => {
  const headers = ['Data', 'Descrição', 'Categoria', 'Tipo', 'Status', 'Valor'];

  const rows = transactions.map(t => [
    formatDate(t.date || t.data),
    `"${(t.description || t.descrição || '').replace(/"/g, '""')}"`,
    t.category || t.categoria || '',
    t.type || t.operacao || '',
    t.status || '',
    parseFloat(t.value || t.valor || t.Valor || 0).toFixed(2).replace('.', ','),
  ]);

  const summaryRows = [
    [],
    ['RESUMO FINANCEIRO'],
    ['Saldo Anterior', '', '', '', '', formatCurrency(balances?.previous)],
    ['Total Recebido', '', '', '', '', formatCurrency(balances?.received)],
    ['A Receber', '', '', '', '', formatCurrency(balances?.toReceive)],
    ['Total Despesas', '', '', '', '', formatCurrency(balances?.expenses)],
    ['A Pagar', '', '', '', '', formatCurrency(balances?.toPay)],
    ['Saldo Previsto', '', '', '', '', formatCurrency(balances?.projected)],
    ['Saldo Real', '', '', '', '', formatCurrency(balances?.actual)],
  ];

  const csvContent = [
    `Walco Finance - Relatório Financeiro`,
    `Período: ${formatDate(period?.start)} a ${formatDate(period?.end)}`,
    `Gerado em: ${new Date().toLocaleString('pt-BR')}`,
    '',
    headers.join(';'),
    ...rows.map(r => r.join(';')),
    ...summaryRows.map(r => r.join(';')),
  ].join('\n');

  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `walco-finance-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ── PDF ──────────────────────────────────────────────────────────────────────
export const exportToPDF = (transactions, balances, period, currentUser) => {
  const doc = new jsPDF({ orientation: 'portrait', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();

  // Background azul escuro no topo
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, pageW, 35, 'F');

  // Título
  doc.setTextColor(59, 130, 246);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Walco Finance', 14, 15);

  doc.setTextColor(148, 163, 184);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Relatório Financeiro', 14, 22);

  const dateStr = new Date().toLocaleString('pt-BR');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(`${currentUser?.name || 'Usuário'}`, pageW - 14, 15, { align: 'right' });
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(148, 163, 184);
  doc.text(`Visão: ${currentUser?.accountType || 'Pessoal'}`, pageW - 14, 20, { align: 'right' });
  doc.text(`Gerado em: ${dateStr}`, pageW - 14, 25, { align: 'right' });

  if (period?.start && period?.end) {
    doc.text(`Período: ${formatDate(period.start)} — ${formatDate(period.end)}`, 14, 29);
  }

  // KPI Cards
  let y = 48; // Significantly reduced from 68/58 to close gap
  doc.setTextColor(30, 41, 59);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text('Resumo Financeiro', 14, y);
  y += 8;

  const kpis = [
    { label: 'Saldo Anterior', value: balances?.previous, color: [100, 116, 139] },
    { label: 'Recebido', value: balances?.received, color: [16, 185, 129] },
    { label: 'A Receber', value: balances?.toReceive, color: [59, 130, 246] },
    { label: 'Despesas', value: balances?.expenses, color: [239, 68, 68] },
    { label: 'A Pagar', value: balances?.toPay, color: [245, 158, 11] },
    { label: 'Saldo Previsto', value: balances?.projected, color: [139, 92, 246] },
    { label: 'Saldo Real', value: balances?.actual, color: [99, 102, 241] },
  ];

  const colW = (pageW - 28) / 4;
  let col = 0;
  let row = 0;

  kpis.forEach((kpi, i) => {
    const x = 14 + col * (colW + 4);
    const cardY = y + row * 18; // Even smaller increment

    // Card background
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(x, cardY, colW, 14, 2, 2, 'F'); // Height to 14

    // Colored bar on left
    doc.setFillColor(...kpi.color);
    doc.roundedRect(x, cardY, 2, 14, 1, 1, 'F');

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(kpi.label, x + 5, cardY + 5); // Title tighter to top

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...kpi.color);
    doc.text(formatCurrency(kpi.value), x + 5, cardY + 11); // Value closer to title

    col++;
    if (col === 4) { col = 0; row++; }
  });

  y += (Math.ceil(kpis.length / 4)) * 18 + 6;

  // Tabela de transações
  doc.setTextColor(30, 41, 59);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text('Transações', 14, y);
  y += 4;

  const tableRows = transactions.map(t => [
    formatDate(t.date || t.data),
    (t.description || t.descrição || '').slice(0, 35),
    t.category || t.categoria || '',
    t.type || t.operacao || '',
    t.status || '',
    formatCurrency(t.value || t.valor || t.Valor),
  ]);

  autoTable(doc, {
    startY: y,
    head: [['Data', 'Descrição', 'Categoria', 'Tipo', 'Status', 'Valor']],
    body: tableRows,
    theme: 'grid',
    headStyles: {
      fillColor: [15, 23, 42],
      textColor: [148, 163, 184],
      fontSize: 9,
      fontStyle: 'bold',
    },
    bodyStyles: {
      fontSize: 9,
      textColor: [30, 41, 59],
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: 22 },
      5: { halign: 'right' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 3) {
        const type = (data.cell.raw || '').toLowerCase();
        if (type === 'receita' || type === 'entrada') {
          data.cell.styles.textColor = [16, 185, 129];
        } else if (type === 'despesa' || type === 'saida') {
          data.cell.styles.textColor = [239, 68, 68];
        }
      }
    },
  });

  // Rodapé
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(
      `Walco Finance — Página ${i} de ${pageCount}`,
      pageW / 2,
      doc.internal.pageSize.getHeight() - 8,
      { align: 'center' }
    );
  }

  doc.save(`walco-finance-${new Date().toISOString().slice(0, 10)}.pdf`);
};
